import React, { useState } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { MetricsStrip } from './MetricsStrip';
import { ContactCard } from './ContactCard';
import { InlineDetailPanel } from './InlineDetailPanel';
import { BottomDock } from './BottomDock';
import { ScheduleFollowUpModal } from './ScheduleFollowUpModal';
import { FundedBorrowers } from './FundedBorrowers';
import { mockLeads, mockLoans, mockRetention } from '../data/mockData';
import { AnyContact, LaneType, PowerDialState, SortOption, Lead, Loan, Retention } from '../types';
import { UsersIcon, FileTextIcon, RepeatIcon } from 'lucide-react';
export function CommandCenter() {
  // Data State
  const [leads, setLeads] = useState(mockLeads);
  const [loans, setLoans] = useState(mockLoans);
  const [retention, setRetention] = useState(mockRetention);
  // UI State
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [activeLane, setActiveLane] = useState<LaneType>('all');
  const [sortBy, setSortBy] = useState<SortOption>('followUp');
  const [selectedContact, setSelectedContact] = useState<AnyContact | null>(null);
  const [powerDial, setPowerDial] = useState<PowerDialState>({
    active: false,
    currentLane: null,
    currentIndex: 0,
    isPaused: false,
    callStartTime: null
  });
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [contactToSchedule, setContactToSchedule] = useState<AnyContact | null>(null);
  // Helpers
  const getLaneData = (lane: LaneType): AnyContact[] => {
    switch (lane) {
      case 'leads':
        return leads;
      case 'loans':
        return loans;
      case 'retention':
        return retention;
      case 'all':
        return [...leads, ...loans, ...retention];
      default:
        return [];
    }
  };
  const sortContacts = (contacts: AnyContact[], sort: SortOption): AnyContact[] => {
    const today = new Date().toISOString().split('T')[0];
    return [...contacts].sort((a, b) => {
      if (sort === 'followUp') {
        const aIsToday = a.scheduledFollowUp === today;
        const bIsToday = b.scheduledFollowUp === today;
        if (aIsToday && !bIsToday) return -1;
        if (!aIsToday && bIsToday) return 1;
        if (a.scheduledFollowUp && b.scheduledFollowUp) {
          return new Date(a.scheduledFollowUp).getTime() - new Date(b.scheduledFollowUp).getTime();
        }
        if (a.scheduledFollowUp) return -1;
        if (b.scheduledFollowUp) return 1;
        return 0;
      }
      if (sort === 'overdue') {
        const aDate = a.history[0]?.date ? new Date(a.history[0].date).getTime() : 0;
        const bDate = b.history[0]?.date ? new Date(b.history[0].date).getTime() : 0;
        return aDate - bDate; // oldest first
      }
      if (sort === 'name') {
        return a.name.localeCompare(b.name);
      }
      if (sort === 'daysSinceContact') {
        const aDays = (a as Lead).daysSinceContact ?? 0;
        const bDays = (b as Lead).daysSinceContact ?? 0;
        return bDays - aDays; // highest first
      }
      if (sort === 'daysInPipeline') {
        const aDays = (a as Loan).daysInPipeline ?? 0;
        const bDays = (b as Loan).daysInPipeline ?? 0;
        return bDays - aDays; // highest first
      }
      if (sort === 'docsRemaining') {
        const aRem = ((a as Loan).docsTotal ?? 0) - ((a as Loan).docsReceived ?? 0);
        const bRem = ((b as Loan).docsTotal ?? 0) - ((b as Loan).docsReceived ?? 0);
        return bRem - aRem; // most incomplete first
      }
      if (sort === 'originalLoanDate') {
        const aDate = (a as Retention).originalLoanDate ? new Date((a as Retention).originalLoanDate).getTime() : 0;
        const bDate = (b as Retention).originalLoanDate ? new Date((b as Retention).originalLoanDate).getTime() : 0;
        return aDate - bDate; // oldest first
      }
      return 0;
    });
  };
  const activeData = sortContacts(getLaneData(activeLane), sortBy);
  // Grouped sections for "All" view
  const sections = [{
    key: 'leads' as const,
    title: 'Follow-Up Queue',
    subtitle: 'Unconverted leads & pre-doc loans',
    icon: UsersIcon,
    data: sortContacts(leads, sortBy),
    color: 'text-cyan-400',
    borderColor: 'border-cyan-500/30',
    bgColor: 'bg-cyan-500/10'
  }, {
    key: 'loans' as const,
    title: 'In-Progress Loans',
    subtitle: 'Awaiting documents & signatures',
    icon: FileTextIcon,
    data: sortContacts(loans, sortBy),
    color: 'text-emerald-400',
    borderColor: 'border-emerald-500/30',
    bgColor: 'bg-emerald-500/10'
  }, {
    key: 'retention' as const,
    title: 'Retention & Repeat',
    subtitle: 'Funded loans for re-engagement',
    icon: RepeatIcon,
    data: sortContacts(retention, sortBy),
    color: 'text-olympian-gold',
    borderColor: 'border-olympian-gold/30',
    bgColor: 'bg-olympian-gold/10'
  }];
  const getRemoveLabel = (contactType: string) => {
    if (contactType === 'lead') return 'Mark DNC';
    if (contactType === 'loan') return 'Withdraw';
    return 'Remove';
  };
  const getLaneFromContactType = (contactType: string): LaneType => {
    if (contactType === 'lead') return 'leads';
    if (contactType === 'loan') return 'loans';
    return 'retention';
  };
  // Actions
  const handleStartPowerDial = () => {
    if (activeData.length === 0) return;
    setPowerDial({
      active: true,
      currentLane: activeLane,
      currentIndex: 0,
      isPaused: false,
      callStartTime: new Date()
    });
    setSelectedContact(activeData[0]);
  };
  const handleEndPowerDial = () => {
    setPowerDial({
      active: false,
      currentLane: null,
      currentIndex: 0,
      isPaused: false,
      callStartTime: null
    });
    setSelectedContact(null);
  };
  const handleNextCall = () => {
    if (!powerDial.currentLane) return;
    const data = sortContacts(getLaneData(powerDial.currentLane), sortBy);
    const nextIndex = powerDial.currentIndex + 1;
    if (nextIndex >= data.length) {
      handleEndPowerDial();
    } else {
      setPowerDial(prev => ({
        ...prev,
        currentIndex: nextIndex,
        isPaused: false,
        callStartTime: new Date()
      }));
      setSelectedContact(data[nextIndex]);
    }
  };
  const handleTogglePause = () => {
    setPowerDial(prev => ({
      ...prev,
      isPaused: !prev.isPaused
    }));
  };
  const handleManualCall = (contact: AnyContact) => {
    if (powerDial.active) handleEndPowerDial();
    setSelectedContact(contact);
  };
  const handleContactClick = (contact: AnyContact) => {
    if (!powerDial.active) {
      setSelectedContact(contact);
    }
  };
  const handleRemoveContact = (contact: AnyContact, lane: LaneType) => {
    const actualLane = lane === 'all' ? getLaneFromContactType(contact.type) : lane;
    if (actualLane === 'leads') setLeads(prev => prev.filter(c => c.id !== contact.id));
    if (actualLane === 'loans') setLoans(prev => prev.filter(c => c.id !== contact.id));
    if (actualLane === 'retention') setRetention(prev => prev.filter(c => c.id !== contact.id));
    if (selectedContact?.id === contact.id) {
      if (powerDial.active) {
        handleNextCall();
      } else {
        setSelectedContact(null);
      }
    }
  };
  const handleAddNote = (contactId: string, noteText: string) => {
    const newNote = {
      id: `note-${Date.now()}`,
      date: new Date().toISOString(),
      type: 'note' as const,
      description: noteText
    };
    const addNoteToContact = (prev: any[]) => prev.map((c: AnyContact) => c.id === contactId ? {
      ...c,
      history: [newNote, ...c.history]
    } : c);
    setLeads(addNoteToContact);
    setLoans(addNoteToContact);
    setRetention(addNoteToContact);
    // Update selectedContact so the panel refreshes
    if (selectedContact?.id === contactId) {
      setSelectedContact({
        ...selectedContact,
        history: [newNote, ...selectedContact.history]
      });
    }
  };
  const handleSaveAndNext = (data: {
    contactId: string;
    nextAction: string;
    followUpDate: string;
  }) => {
    // Add next action as a note
    handleAddNote(data.contactId, `Next Action: ${data.nextAction}`);
    // Set follow-up date on the contact
    const updateFollowUp = (prev: any[]) => prev.map((c: AnyContact) => c.id === data.contactId ? {
      ...c,
      scheduledFollowUp: data.followUpDate
    } : c);
    setLeads(updateFollowUp);
    setLoans(updateFollowUp);
    setRetention(updateFollowUp);
    // Advance to next call
    handleNextCall();
  };
  const handleOpenSchedule = (contact: AnyContact) => {
    setContactToSchedule(contact);
    setScheduleModalVisible(true);
  };
  const handleSaveSchedule = (contactId: string, date: string, nextAction: string) => {
    if (contactToSchedule) {
      const updateContact = (prev: AnyContact[]) => prev.map(c => c.id === contactId ? {
        ...c,
        scheduledFollowUp: date
      } : c);
      const lane = getLaneFromContactType(contactToSchedule.type);
      if (lane === 'leads') setLeads(updateContact as any);
      if (lane === 'loans') setLoans(updateContact as any);
      if (lane === 'retention') setRetention(updateContact as any);
    }
    setScheduleModalVisible(false);
    setContactToSchedule(null);
  };
  const renderContactList = () => {
    if (activeLane === 'all') {
      return <>
          {sections.map(section => {
          if (section.data.length === 0) return null;
          const Icon = section.icon;
          return <div key={section.key} className="mb-6 last:mb-0" data-id="element-15">
                {/* Section Divider */}
                <div className="flex items-center gap-3 mb-3 px-1 sticky top-0 z-10 py-2 backdrop-blur-sm" style={{
              backgroundColor: 'var(--color-divider-bg)'
            }} data-id="element-16">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${section.borderColor} ${section.bgColor}`} data-id="element-17">
                    <Icon className={`w-4 h-4 ${section.color}`} data-id="element-18" />
                    <span className={`text-sm font-semibold ${section.color}`} data-id="element-19">
                      {section.title}
                    </span>
                    <span className={`text-xs font-mono font-bold ${section.color} ml-1`} data-id="element-20">
                      {section.data.length}
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-olympian-border" data-id="element-21"></div>
                </div>
                {/* Section Contacts */}
                <div className="space-y-3" data-id="element-22">
                  {section.data.map(contact => <ContactCard key={contact.id} contact={contact} isActive={selectedContact?.id === contact.id} onClick={handleContactClick} onCall={handleManualCall} onSchedule={section.key !== 'loans' ? handleOpenSchedule : undefined} onRemove={c => handleRemoveContact(c, section.key)} removeLabel={getRemoveLabel(contact.type)} data-id="element-23" />)}
                </div>
              </div>;
        })}
          {activeData.length === 0 && <div className="h-full flex items-center justify-center text-olympian-textMuted" data-id="element-24">
              No contacts in the pipeline.
            </div>}
        </>;
    }
    return <>
        {activeData.map(contact => <ContactCard key={contact.id} contact={contact} isActive={selectedContact?.id === contact.id} onClick={handleContactClick} onCall={handleManualCall} onSchedule={activeLane !== 'loans' ? handleOpenSchedule : undefined} onRemove={c => handleRemoveContact(c, activeLane)} removeLabel={activeLane === 'leads' ? 'Mark DNC' : activeLane === 'loans' ? 'Withdraw' : 'Remove'} data-id="element-25" />)}
        {activeData.length === 0 && <div className="h-full flex items-center justify-center text-olympian-textMuted" data-id="element-26">
            No contacts in this queue.
          </div>}
      </>;
  };
  return <div className={`flex flex-col h-screen w-full overflow-hidden transition-colors duration-300 ${isDarkMode ? '' : 'light-mode'}`} style={{
    backgroundColor: 'var(--color-bg-app)'
  }} data-id="element-27">
      <Header isDarkMode={isDarkMode} onToggleDarkMode={() => setIsDarkMode(!isDarkMode)} data-id="element-28" />

      <div className="flex flex-1 overflow-hidden" data-id="element-29">
        <Sidebar activeLane={activeLane} setActiveLane={lane => {
        setActiveLane(lane);
        setSortBy('followUp');
        if (!powerDial.active) setSelectedContact(null);
      }} counts={{
        leads: leads.length,
        loans: loans.length,
        retention: retention.length
      }} data-id="element-30" />

        <div className="flex flex-col flex-1 overflow-hidden relative" data-id="element-31">
          {activeLane !== 'retention' && <MetricsStrip laneType={activeLane} data={activeData} allData={{
          leads,
          loans,
          retention
        }} onPowerDial={handleStartPowerDial} isPowerDialing={powerDial.active} isPaused={powerDial.isPaused} onPauseToggle={handleTogglePause} onEndSession={handleEndPowerDial} sortBy={sortBy} onSortChange={setSortBy} data-id="element-32" />}

          <div className="flex flex-1 overflow-hidden" data-id="element-33">
            {activeLane === 'retention' ? <div className="flex-1 overflow-y-auto custom-scrollbar bg-theme-app" data-id="element-34">
                <FundedBorrowers data={retention} data-id="element-35" />
              </div> : <>
                {/* Contact List Area */}
                <div className={`flex-1 overflow-y-auto p-6 space-y-3 custom-scrollbar transition-all duration-300 ${selectedContact ? 'max-w-[60%]' : 'max-w-full'}`} data-id="element-36">
                  {renderContactList()}
                </div>

                {/* Inline Detail Panel */}
                {selectedContact && <div className="w-[40%] border-l border-olympian-border bg-olympian-surface/30 animate-crossfade shrink-0" data-id="element-37">
                    <InlineDetailPanel contact={selectedContact} onClose={() => !powerDial.active && setSelectedContact(null)} onAddNote={handleAddNote} isPowerDialing={powerDial.active} onSaveAndNext={handleSaveAndNext} data-id="element-38" />
                  </div>}
              </>}
          </div>
        </div>
      </div>

      {powerDial.active && powerDial.currentLane && <BottomDock activeContact={selectedContact} currentIndex={powerDial.currentIndex} totalInLane={getLaneData(powerDial.currentLane).length} isPaused={powerDial.isPaused} data-id="element-39" />}

      <ScheduleFollowUpModal contact={contactToSchedule} isVisible={scheduleModalVisible} onClose={() => setScheduleModalVisible(false)} onSave={handleSaveSchedule} data-id="element-40" />
    </div>;
}